import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Space, Modal, Form, Input, Select, DatePicker, InputNumber, message, Tag, Row, Col, Divider, Card, Popconfirm } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined, CheckOutlined, SearchOutlined, ClearOutlined, FilterOutlined, SyncOutlined, FilePdfOutlined } from '@ant-design/icons';
import { despesaService, categoriaDespesaService, fornecedorService, unidadeService, relatorioService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import dayjs from 'dayjs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const DespesasList = () => {
  const { user } = useAuth();
  const [despesas, setDespesas] = useState([]);
  const [despesasFiltradas, setDespesasFiltradas] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [fornecedores, setFornecedores] = useState([]);
  const [unidades, setUnidades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isBaixaModalVisible, setIsBaixaModalVisible] = useState(false);
  const [selectedDespesa, setSelectedDespesa] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [mostrarFiltros, setMostrarFiltros] = useState(true);
  const [form] = Form.useForm();
  const [baixaForm] = Form.useForm();
  const [filtros, setFiltros] = useState({
    busca_geral: '',
    id_categoria: null,
    id_fornecedor: null,
    id_unidades: null,
    status: null,
    mes_vencimento: null,
  });

  const compareText = (a, b) =>
    (a || '').toString().localeCompare((b || '').toString(), 'pt-BR', { sensitivity: 'base' });

  useEffect(() => {
    fetchDespesas();
    fetchCategorias();
    fetchFornecedores();
    fetchUnidades();
  }, []);

  const aplicarFiltros = useCallback(() => {
    let resultado = [...despesas];

    if (filtros.id_categoria) {
      resultado = resultado.filter(d => d.id_categoria === filtros.id_categoria);
    }

    if (filtros.id_fornecedor) {
      resultado = resultado.filter(d => d.id_fornecedor === filtros.id_fornecedor);
    }

    if (filtros.id_unidades) {
      resultado = resultado.filter(d => d.id_unidades === filtros.id_unidades);
    }

    if (filtros.status) {
      resultado = resultado.filter(d => d.status === filtros.status);
    }

    if (filtros.mes_vencimento) {
      resultado = resultado.filter(d => {
        if (!d.data_vencimento) return false;
        const mes = dayjs(d.data_vencimento).format('YYYY-MM');
        return mes === filtros.mes_vencimento;
      });
    }

    if (filtros.busca_geral) {
      const termo = filtros.busca_geral.toLowerCase();
      resultado = resultado.filter(d => {
        const descricao = d.descricao ? d.descricao.toLowerCase() : '';
        const numeroDoc = d.numero_documento ? d.numero_documento.toLowerCase() : '';
        return descricao.includes(termo) || numeroDoc.includes(termo);
      });
    }

    setDespesasFiltradas(resultado);
  }, [despesas, filtros]);

  useEffect(() => {
    aplicarFiltros();
  }, [aplicarFiltros]);

  const handleFiltroChange = (campo, valor) => {
    setFiltros(prev => ({ ...prev, [campo]: valor }));
  };

  const limparFiltros = () => {
    setFiltros({
      busca_geral: '',
      id_categoria: null,
      id_fornecedor: null,
      id_unidades: null,
      status: null,
      mes_vencimento: null,
    });
    message.success('Filtros limpos');
  };

  const fetchDespesas = async () => {
    setLoading(true);
    try {
      const response = await despesaService.list();
      setDespesas(response.data);
    } catch (error) {
      message.error('Erro ao carregar despesas');
      console.error(error);
    }
    setLoading(false);
  };

  const fetchCategorias = async () => {
    try {
      const response = await categoriaDespesaService.list({ ativo: true });
      setCategorias(response.data);
    } catch (error) {
      console.error('Erro ao carregar categorias');
    }
  };

  const fetchFornecedores = async () => {
    try {
      const response = await fornecedorService.list({ ativo: true });
      setFornecedores(response.data);
    } catch (error) {
      console.error('Erro ao carregar fornecedores');
    }
  };

  const fetchUnidades = async () => {
    try {
      const response = await unidadeService.list();
      setUnidades(response.data);
    } catch (error) {
      console.error('Erro ao carregar unidades');
    }
  };

  const handleAdd = () => {
    form.resetFields();
    setEditingId(null);
    setIsModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingId(record.id);
    const formData = {
      ...record,
      data_vencimento: record.data_vencimento ? dayjs(record.data_vencimento) : null,
      data_pagamento: record.data_pagamento ? dayjs(record.data_pagamento) : null,
      data_inicio_repasse: record.data_inicio_repasse ? dayjs(record.data_inicio_repasse) : null,
      data_fim_repasse: record.data_fim_repasse ? dayjs(record.data_fim_repasse) : null,
    };
    form.setFieldsValue(formData);
    setIsModalVisible(true);
  };

  const handleDelete = async (id) => {
    Modal.confirm({
      title: 'Confirmação',
      content: 'Deseja excluir esta despesa?',
      okText: 'Sim',
      cancelText: 'Não',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await despesaService.delete(id);
          message.success('Despesa excluída com sucesso');
          fetchDespesas();
        } catch (error) {
          message.error('Erro ao excluir despesa');
        }
      },
    });
  };

  const handleDarBaixa = (record) => {
    setSelectedDespesa(record);
    baixaForm.setFieldsValue({
      data_pagamento: dayjs(),
      forma_pagamento: null,
      observacoes: null,
    });
    setIsBaixaModalVisible(true);
  };

  const handleSubmitBaixa = async (values) => {
    try {
      const submitData = {
        ...values,
        data_pagamento: values.data_pagamento
          ? values.data_pagamento.format('YYYY-MM-DD')
          : null,
      };
      await despesaService.darBaixa(selectedDespesa.id, submitData);
      message.success('Baixa realizada com sucesso');
      setIsBaixaModalVisible(false);
      fetchDespesas();
    } catch (error) {
      message.error('Erro ao dar baixa na despesa');
      console.error(error);
    }
  };

  const handleReverterBaixa = async (despesaId) => {
    try {
      await despesaService.reverterBaixa(despesaId);
      message.success('Baixa revertida com sucesso - status voltou para PENDENTE');
      fetchDespesas();
    } catch (error) {
      message.error('Erro ao reverter baixa da despesa');
    }
  };

  const handleValuesChange = (changedValues, allValues) => {
    // Calcular valor da parcela quando valor_total ou qtd_parcelas mudar
    if ('valor_total' in changedValues || 'qtd_parcelas' in changedValues) {
      if (allValues.valor_total) {
        const parcelas = allValues.qtd_parcelas && allValues.qtd_parcelas > 0
          ? allValues.qtd_parcelas
          : 1;
        const valorParcela = allValues.valor_total / parcelas;
        form.setFieldsValue({ valor: valorParcela });
      }
    }
  };

  const handleSubmit = async (values) => {
    try {
      const submitData = {
        ...values,
        data_vencimento: values.data_vencimento ? values.data_vencimento.format('YYYY-MM-DD') : null,
        data_pagamento: values.data_pagamento ? values.data_pagamento.format('YYYY-MM-DD') : null,
        data_inicio_repasse: values.data_inicio_repasse ? values.data_inicio_repasse.format('YYYY-MM-DD') : null,
        data_fim_repasse: values.data_fim_repasse ? values.data_fim_repasse.format('YYYY-MM-DD') : null,
      };

      if (submitData.valor_total && (!submitData.qtd_parcelas || submitData.qtd_parcelas <= 1)) {
        submitData.valor = submitData.valor_total;
      }

      if (editingId) {
        await despesaService.update(editingId, submitData);
        message.success('Despesa atualizada com sucesso');
      } else {
        const response = await despesaService.create(submitData);
        message.success('Despesa criada com sucesso');
      }
      setIsModalVisible(false);
      fetchDespesas();
    } catch (error) {
      message.error('Erro ao salvar despesa');
      console.error(error);
    }
  };

  const getCategoriaNome = (id) => {
    const categoria = categorias.find(c => c.id === id);
    return categoria ? categoria.nome : '-';
  };

  const getFornecedorNome = (id) => {
    if (!id) return '-';
    const fornecedor = fornecedores.find(f => f.id === id);
    return fornecedor ? fornecedor.nome_razao : '-';
  };

  const getUnidadeNome = (id) => {
    if (!id) return '-';
    const unidade = unidades.find(u => u.id === id);
    return unidade ? unidade.nome_fantasia : '-';
  };

  const isCategoriaRepasseGlobal = (categoriaId) => {
    const categoria = categorias.find(c => c.id === categoriaId);
    const nome = (categoria?.nome || categoria?.nome_categoria || '').trim().toLowerCase();
    return nome === 'repasse global';
  };

  const formatCurrency = (value) => `R$ ${(Number(value) || 0).toFixed(2)}`;

  const carregarLogo = async () => {
    try {
      const response = await fetch(`${process.env.PUBLIC_URL}/logo-fisicore.png`);
      if (!response.ok) return null;
      const buffer = await response.arrayBuffer();
      const bytes = new Uint8Array(buffer);

      const isPng =
        bytes.length >= 8 &&
        bytes[0] === 0x89 &&
        bytes[1] === 0x50 &&
        bytes[2] === 0x4e &&
        bytes[3] === 0x47 &&
        bytes[4] === 0x0d &&
        bytes[5] === 0x0a &&
        bytes[6] === 0x1a &&
        bytes[7] === 0x0a;
      const isJpeg = bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff;

      const mime = isJpeg ? 'image/jpeg' : isPng ? 'image/png' : null;
      if (!mime) return null;

      const blob = new Blob([buffer], { type: mime });
      const format = isJpeg ? 'JPEG' : 'PNG';

      return await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve({ dataUrl: reader.result, format });
        reader.readAsDataURL(blob);
      });
    } catch {
      return null;
    }
  };

  const gerarPdfRepasseGlobal = async (relatorio) => {
    try {
      const doc = new jsPDF();
      const logo = await carregarLogo();
      if (logo?.dataUrl) {
        doc.addImage(logo.dataUrl, logo.format || 'PNG', 14, 10, 24, 24);
      }

      doc.setTextColor(0);
      doc.setFontSize(14);
      doc.text('RELATÓRIO DE REPASSE GLOBAL', 105, 16, { align: 'center' });

      doc.setFontSize(10);
      let y = 28;
      doc.text(`Unidade: ${relatorio.unidade_nome || '-'}`, 14, y);
      y += 6;
      doc.text(`Competência: ${dayjs(relatorio.competencia + '-01').format('MMMM/YYYY')}`, 14, y);
      y += 6;
      doc.text(`Alunos ativos: ${relatorio.alunos_ativos ?? 0}`, 14, y);
      y += 6;
      doc.text(`Percentual de repasse: ${(relatorio.repasse_percentual || 0).toFixed(2)}%`, 14, y);
      y += 8;

      autoTable(doc, {
        startY: y,
        head: [[
          'Total receita prevista',
          'Total receita recebida',
          'Repasse previsto',
          'Repasse recebido',
        ]],
        body: [[
          formatCurrency(relatorio.total_receita_prevista),
          formatCurrency(relatorio.total_receita_recebida),
          formatCurrency(relatorio.valor_repasse_previsto),
          formatCurrency(relatorio.valor_repasse_recebido),
        ]],
        styles: { fontSize: 9 },
      });

      const dataEmissao = dayjs().format('DD/MM/YYYY');
      const finalY = doc.lastAutoTable?.finalY || y + 20;
      doc.setFontSize(9);
      doc.text(`Emitido em: ${dataEmissao}`, 14, finalY + 10);

      const nomeArquivo = `Repasse-Global_${(relatorio.unidade_nome || 'unidade').replace(/\s+/g, '_')}_${relatorio.competencia}.pdf`;
      doc.save(nomeArquivo);
    } catch (error) {
      console.error('Erro ao gerar PDF repasse global', error);
      throw error;
    }
  };

  const gerarPdfReciboDespesa = async (despesa, extras = {}) => {
    try {
      const doc = new jsPDF();
      const logo = await carregarLogo();
      if (logo?.dataUrl) {
        doc.addImage(logo.dataUrl, logo.format || 'PNG', 14, 10, 24, 24);
      }

      doc.setTextColor(0);
      doc.setFontSize(14);
      doc.text('RECIBO DE DESPESA', 105, 16, { align: 'center' });

      doc.setFontSize(10);
      let y = 28;
      doc.text(`Fornecedor: ${getFornecedorNome(despesa.id_fornecedor)}`, 14, y);
      y += 6;
      doc.text(`Categoria: ${getCategoriaNome(despesa.id_categoria)}`, 14, y);
      y += 6;
      doc.text(`Unidade: ${getUnidadeNome(despesa.id_unidade || despesa.id_unidades)}`, 14, y);
      y += 6;
      doc.text(`Número do Documento: ${despesa.numero_documento || '-'}`, 14, y);
      y += 8;

      const qtdParcelas = despesa.qtd_parcelas || 1;
      const valorTotal = despesa.valor_total
        ?? (despesa.valor ? despesa.valor * qtdParcelas : 0);
      const valorParcela = qtdParcelas > 0 ? valorTotal / qtdParcelas : 0;

      autoTable(doc, {
        startY: y,
        head: [['Valor Total', 'Qtd. Parcelas', 'Valor da Parcela']],
        body: [[
          formatCurrency(valorTotal),
          `${qtdParcelas}`,
          formatCurrency(valorParcela),
        ]],
        styles: { fontSize: 9 },
      });

      let finalY = doc.lastAutoTable?.finalY || y + 20;

      if (extras?.matriculas_ativas !== undefined) {
        finalY += 8;
        doc.setFontSize(10);
        doc.text('Relatório - Matrículas Ativas', 14, finalY);
        finalY += 6;
        if (extras.periodo_inicio && extras.periodo_fim) {
          doc.text(
            `Período: ${dayjs(extras.periodo_inicio).format('DD/MM/YYYY')} a ${dayjs(extras.periodo_fim).format('DD/MM/YYYY')}`,
            14,
            finalY
          );
          finalY += 6;
        }
        doc.text(`Alunos ativos: ${extras.alunos_ativos ?? 0}`, 14, finalY);
        finalY += 6;
        doc.text(`Matrículas ativas: ${extras.matriculas_ativas ?? 0}`, 14, finalY);
      }

      const dataEmissao = dayjs().format('DD/MM/YYYY');
      doc.setFontSize(9);
      doc.text(`Emitido em: ${dataEmissao}`, 14, (finalY || y) + 10);

      const nomeArquivo = `Recibo_Despesa_${despesa.id}.pdf`;
      doc.save(nomeArquivo);
    } catch (error) {
      console.error('Erro ao gerar recibo de despesa', error);
      throw error;
    }
  };

  const abrirRecibo = (despesa) => {
    if (isCategoriaRepasseGlobal(despesa.id_categoria)) {
      if (!despesa.id_unidade && !despesa.id_unidades) {
        message.warning('Para Repasse Global, selecione a unidade antes de gerar o recibo.');
        return;
      }
      if (!despesa.data_inicio_repasse || !despesa.data_fim_repasse) {
        message.warning('Informe data início e data fim no cadastro da despesa para gerar o recibo de repasse global.');
        return;
      }

      const inicio = dayjs(despesa.data_inicio_repasse);
      const fim = dayjs(despesa.data_fim_repasse);

      relatorioService
        .matriculasAtivas({
          id_unidade: despesa.id_unidade || despesa.id_unidades,
          data_inicio: inicio.format('YYYY-MM-DD'),
          data_fim: fim.format('YYYY-MM-DD'),
        })
        .then((response) =>
          gerarPdfReciboDespesa(despesa, {
            periodo_inicio: inicio,
            periodo_fim: fim,
            alunos_ativos: response.data?.alunos_ativos ?? 0,
            matriculas_ativas: response.data?.matriculas_ativas ?? 0,
          })
        )
        .catch((error) => {
          const detalhe = error?.response?.data?.detail;
          message.error(detalhe || error?.message || 'Erro ao gerar recibo de repasse global');
        });
      return;
    }

    gerarPdfReciboDespesa(despesa).catch((error) => {
      const detalhe = error?.response?.data?.detail;
      message.error(detalhe || error?.message || 'Erro ao gerar recibo da despesa');
    });
  };

  const gerarRelatorioRepasseGlobal = async (despesa) => {
    const competencia = despesa.competencia
      || (despesa.data_vencimento ? dayjs(despesa.data_vencimento).format('YYYY-MM') : null);

    if (!despesa.id_unidade) {
      message.warning('Para Repasse Global, selecione a unidade antes de gerar o relatório.');
      return;
    }
    if (!competencia) {
      message.warning('Competência não encontrada para gerar o relatório.');
      return;
    }

    const response = await relatorioService.repasseGlobal({
      id_unidade: despesa.id_unidade,
      competencia,
    });
    await gerarPdfRepasseGlobal(response.data);
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 50,
      sorter: (a, b) => (Number(a?.id) || 0) - (Number(b?.id) || 0),
    },
    {
      title: 'Data Vencimento',
      dataIndex: 'data_vencimento',
      key: 'data_vencimento',
      width: 120,
      render: (date) => date ? dayjs(date).format('DD/MM/YYYY') : '-',
      sorter: (a, b) => {
        const timeA = a?.data_vencimento ? dayjs(a.data_vencimento).valueOf() : Number.NEGATIVE_INFINITY;
        const timeB = b?.data_vencimento ? dayjs(b.data_vencimento).valueOf() : Number.NEGATIVE_INFINITY;
        return timeA - timeB;
      },
    },
    {
      title: 'Descrição',
      dataIndex: 'descricao',
      key: 'descricao',
      sorter: (a, b) => compareText(a.descricao, b.descricao),
    },
    {
      title: 'Categoria',
      dataIndex: 'id_categoria',
      key: 'id_categoria',
      render: (id) => getCategoriaNome(id),
      sorter: (a, b) => compareText(getCategoriaNome(a.id_categoria), getCategoriaNome(b.id_categoria)),
    },
    {
      title: 'Fornecedor',
      dataIndex: 'id_fornecedor',
      key: 'id_fornecedor',
      render: (id) => getFornecedorNome(id),
    },
    {
      title: 'Valor',
      dataIndex: 'valor',
      key: 'valor',
      width: 120,
      render: (valor) => `R$ ${valor?.toFixed(2)}`,
    },
    {
      title: 'Status',
      dataIndex: 'pago',
      key: 'pago',
      width: 100,
      render: (pago) => (
        <Tag color={pago ? 'green' : 'orange'}>
          {pago ? 'Pago' : 'Pendente'}
        </Tag>
      ),
    },
    {
      title: 'Ações',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space>
          <Button
            icon={<FilePdfOutlined />}
            onClick={() => abrirRecibo(record)}
            size="small"
            title="Gerar recibo"
          />
          {!record.pago && (
            <Button
              icon={<CheckOutlined />}
              onClick={() => handleDarBaixa(record)}
              size="small"
              type="primary"
              title="Dar Baixa"
            />
          )}
          {record.pago && user?.role === 'ADMIN' && (
            <Popconfirm
              title="Tem certeza que deseja reverter esta baixa? O status voltará para PENDENTE."
              onConfirm={() => handleReverterBaixa(record.id)}
              okText="Sim"
              cancelText="Não"
            >
              <Button
                icon={<SyncOutlined />}
                size="small"
                type="default"
                title="Reverter"
              >
                Reverter
              </Button>
            </Popconfirm>
          )}
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          />
          <Button
            icon={<DeleteOutlined />}
            danger
            onClick={() => handleDelete(record.id)}
            size="small"
          />
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* Painel de Filtros */}
      <Card 
        title={
          <Space>
            <FilterOutlined />
            Filtros de Busca
            <span style={{ fontSize: '12px', fontWeight: 'normal', color: '#888' }}>
              ({despesasFiltradas.length} de {despesas.length} despesas)
            </span>
          </Space>
        }
        extra={
          <Button type="text" size="small" onClick={() => setMostrarFiltros(!mostrarFiltros)}>
            {mostrarFiltros ? 'Ocultar' : 'Mostrar'}
          </Button>
        }
        style={{ marginBottom: 16 }}
      >
        {mostrarFiltros && (
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8} lg={6}>
              <div style={{ marginBottom: 4, fontWeight: 500 }}>Busca Geral</div>
              <Input
                placeholder="Pesquisar por descrição, documento..."
                prefix={<SearchOutlined />}
                value={filtros.busca_geral}
                onChange={(e) => handleFiltroChange('busca_geral', e.target.value)}
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <div style={{ marginBottom: 4, fontWeight: 500 }}>Categoria</div>
              <Select
                placeholder="Todas as categorias"
                value={filtros.id_categoria}
                onChange={(value) => handleFiltroChange('id_categoria', value)}
                allowClear
                showSearch
                optionFilterProp="children"
                style={{ width: '100%' }}
              >
                {categorias.map(c => (
                  <Select.Option key={c.id} value={c.id}>{c.nome_categoria}</Select.Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <div style={{ marginBottom: 4, fontWeight: 500 }}>Fornecedor</div>
              <Select
                placeholder="Todos os fornecedores"
                value={filtros.id_fornecedor}
                onChange={(value) => handleFiltroChange('id_fornecedor', value)}
                allowClear
                showSearch
                optionFilterProp="children"
                style={{ width: '100%' }}
              >
                {fornecedores.map(f => (
                  <Select.Option key={f.id} value={f.id}>{f.nome_fornecedor}</Select.Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <div style={{ marginBottom: 4, fontWeight: 500 }}>Unidade</div>
              <Select
                placeholder="Todas as unidades"
                value={filtros.id_unidades}
                onChange={(value) => handleFiltroChange('id_unidades', value)}
                allowClear
                showSearch
                optionFilterProp="children"
                style={{ width: '100%' }}
              >
                {unidades.map(u => (
                  <Select.Option key={u.id} value={u.id}>{u.nome_fantasia}</Select.Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <div style={{ marginBottom: 4, fontWeight: 500 }}>Status</div>
              <Select
                placeholder="Todos os status"
                value={filtros.status}
                onChange={(value) => handleFiltroChange('status', value)}
                allowClear
                style={{ width: '100%' }}
              >
                <Select.Option value="PENDENTE">Pendente</Select.Option>
                <Select.Option value="PAGO">Pago</Select.Option>
                <Select.Option value="VENCIDO">Vencido</Select.Option>
                <Select.Option value="CANCELADO">Cancelado</Select.Option>
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <div style={{ marginBottom: 4, fontWeight: 500 }}>Mês de Vencimento</div>
              <DatePicker
                placeholder="Selecione o mês"
                picker="month"
                value={filtros.mes_vencimento ? dayjs(filtros.mes_vencimento) : null}
                onChange={(date) => handleFiltroChange('mes_vencimento', date ? date.format('YYYY-MM') : null)}
                style={{ width: '100%' }}
                format="MM/YYYY"
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <div style={{ marginBottom: 4, fontWeight: 500 }}>&nbsp;</div>
              <Button icon={<ClearOutlined />} onClick={limparFiltros} block>
                Limpar Filtros
              </Button>
            </Col>
          </Row>
        )}
      </Card>

      <div style={{ marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          Nova Despesa
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={despesasFiltradas}
        loading={loading}
        rowKey="id"
        pagination={{ 
          pageSize: 10,
          showSizeChanger: true,
          showTotal: (total) => `Total de ${total} despesas`,
          pageSizeOptions: ['10', '20', '50', '100']
        }}
        scroll={{ x: 1400 }}
      />

      <Modal
        title={editingId ? 'Editar Despesa' : 'Nova Despesa'}
        open={isModalVisible}
        onOk={() => form.submit()}
        onCancel={() => setIsModalVisible(false)}
        width={900}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          onValuesChange={handleValuesChange}
          initialValues={{ pago: false }}
        >
          <Divider>Informações Básicas</Divider>

          <Form.Item
            name="descricao"
            label="Descrição"
            rules={[{ required: true, message: 'Descrição é obrigatória' }]}
          >
            <Input placeholder="Descrição da despesa" />
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="id_categoria"
                label="Categoria"
                rules={[{ required: true, message: 'Categoria é obrigatória' }]}
              >
                <Select placeholder="Selecione a categoria">
                  {categorias.map((cat) => (
                    <Select.Option key={cat.id} value={cat.id}>
                      {cat.nome}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="id_fornecedor" label="Fornecedor">
                <Select placeholder="Selecione o fornecedor (opcional)">
                  {fornecedores.map((forn) => (
                    <Select.Option key={forn.id} value={forn.id}>
                      {forn.nome_razao}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="id_unidade"
                label="Unidade"
                dependencies={['id_categoria']}
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const categoriaId = getFieldValue('id_categoria');
                      if (isCategoriaRepasseGlobal(categoriaId) && !value) {
                        return Promise.reject(new Error('Unidade é obrigatória para Repasse Global'));
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <Select placeholder="Selecione a unidade (opcional)">
                  {unidades.map((unid) => (
                    <Select.Option key={unid.id} value={unid.id}>
                      {unid.nome_fantasia}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="numero_documento" label="Número do Documento">
                <Input placeholder="NF, recibo, etc." />
              </Form.Item>
            </Col>
          </Row>

          <Divider>Valores e Parcelamento</Divider>

          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item
                name="valor_total"
                label="Valor Total (R$)"
                rules={[{ required: true, message: 'Valor é obrigatório' }]}
              >
                <InputNumber
                  min={0}
                  step={0.01}
                  precision={2}
                  placeholder="0.00"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item 
                name="qtd_parcelas" 
                label="Quantidade de Parcelas"
                help="Deixe em branco ou 1 para pagamento único"
              >
                <InputNumber 
                  min={1} 
                  placeholder="1" 
                  style={{ width: '100%' }} 
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item 
                name="valor" 
                label="Valor da Parcela (R$)"
                help="Calculado automaticamente"
              >
                <InputNumber
                  min={0}
                  step={0.01}
                  precision={2}
                  placeholder="0.00"
                  style={{ width: '100%' }}
                  disabled
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider>Datas</Divider>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="data_vencimento"
                label="Data de Vencimento"
                rules={[{ required: true, message: 'Data é obrigatória' }]}
              >
                <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="data_pagamento" label="Data de Pagamento">
                <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item shouldUpdate={(prev, cur) => prev.id_categoria !== cur.id_categoria}>
            {({ getFieldValue }) =>
              isCategoriaRepasseGlobal(getFieldValue('id_categoria')) ? (
                <Row gutter={16}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="data_inicio_repasse"
                      label="Data Início (Repasse Global)"
                      rules={[{ required: true, message: 'Data início é obrigatória' }]}
                    >
                      <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="data_fim_repasse"
                      label="Data Fim (Repasse Global)"
                      rules={[{ required: true, message: 'Data fim é obrigatória' }]}
                    >
                      <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                </Row>
              ) : null
            }
          </Form.Item>

          <Divider>Detalhes</Divider>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="forma_pagamento" label="Forma de Pagamento">
                <Select placeholder="Selecione a forma de pagamento">
                  <Select.Option value="DINHEIRO">Dinheiro</Select.Option>
                  <Select.Option value="CARTAO_CREDITO">Cartão de Crédito</Select.Option>
                  <Select.Option value="CARTAO_DEBITO">Cartão de Débito</Select.Option>
                  <Select.Option value="BOLETO">Boleto</Select.Option>
                  <Select.Option value="PIX">PIX</Select.Option>
                  <Select.Option value="TRANSFERENCIA">Transferência</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="observacoes" label="Observações">
            <Input.TextArea rows={3} placeholder="Observações adicionais" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Dar Baixa na Despesa"
        open={isBaixaModalVisible}
        onOk={() => baixaForm.submit()}
        onCancel={() => setIsBaixaModalVisible(false)}
        okText="Confirmar Pagamento"
        cancelText="Cancelar"
        width={500}
      >
        {selectedDespesa && (
          <div style={{ marginBottom: 16, padding: 12, background: '#f0f2f5', borderRadius: 4 }}>
            <p style={{ margin: '0 0 4px' }}><strong>Descrição:</strong> {selectedDespesa.descricao}</p>
            <p style={{ margin: '0 0 4px' }}><strong>Vencimento:</strong> {dayjs(selectedDespesa.data_vencimento).format('DD/MM/YYYY')}</p>
            <p style={{ margin: 0 }}><strong>Valor:</strong> R$ {selectedDespesa.valor?.toFixed(2)}</p>
          </div>
        )}
        <Form form={baixaForm} layout="vertical" onFinish={handleSubmitBaixa}>
          <Form.Item
            name="data_pagamento"
            label="Data de Pagamento"
            rules={[{ required: true, message: 'Data de pagamento é obrigatória' }]}
          >
            <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="forma_pagamento" label="Forma de Pagamento">
            <Select placeholder="Selecione (opcional)">
              <Select.Option value="DINHEIRO">Dinheiro</Select.Option>
              <Select.Option value="CARTAO_CREDITO">Cartão de Crédito</Select.Option>
              <Select.Option value="CARTAO_DEBITO">Cartão de Débito</Select.Option>
              <Select.Option value="BOLETO">Boleto</Select.Option>
              <Select.Option value="PIX">PIX</Select.Option>
              <Select.Option value="TRANSFERENCIA">Transferência</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="observacoes" label="Observações">
            <Input.TextArea rows={3} placeholder="Observações sobre o pagamento" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default DespesasList;
